<script setup lang="ts">
import { ref } from "vue";
const switchLocalePath = useSwitchLocalePath();

const theme = ref(useNuxtApp().$colorMode.preference);
const active = ref(0);
const tempLanguages = ref([
  {
    name: "header.countries.brazil",
    name_abrev: "pt-BR",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/255-brazil.svg",
    native_name: "Português",
    localized_name: "Portuguese",
  },
  {
    name: "header.countries.germany",
    name_abrev: "de",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/162-germany.svg",
    native_name: "Deutsch",
    localized_name: "German",
  },
  {
    name: "header.countries.spain",
    name_abrev: "es",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/128-spain.svg",
    native_name: "Español",
    localized_name: "Spanish",
  },
  {
    name: "header.countries.romania",
    name_abrev: "ro",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/109-romania.svg",
    native_name: "Rom\u00e2n\u0103",
    localized_name: "Romanian",
  },
  {
    name: "header.countries.united_arab_emirates",
    name_abrev: "ar-AE",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/151-united-arab-emirates.svg",
    native_name: "العربية",
    localized_name: "Arabic",
  },
  {
    name: "header.countries.united_states",
    name_abrev: "en",
    flag_url:
      "https://storage.mundogamer.network/mg-public/countries/flags-svg/226-united-states.svg",
    native_name: "English",
    localized_name: "English",
  },
]);

// $REFS

const container = ref();
const wrapper = ref();

// MOUNTED
onMounted(() => {
  setHeight();
  scrollWrapper();
});

// WATCHERS

watch(theme, (themeChanged) => {
  useNuxtApp().$colorMode.preference = themeChanged;
});

// METHODS/FUNCTIONS

function setHeight() {
  const elIndex = active.value;
  const el = wrapper.value.children[elIndex];
  const height = el.scrollHeight;
  container.value.style.height = height + 32 + "px";
}

function scrollWrapper() {
  const offset = 100 * active.value;
  const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
  const direction = isRtl ? '' : '-';
  wrapper.value.style.transform = `translate3d(${direction}${offset}%, 0, 0)`;
}

function changeTab(i: number) {
  active.value = i;
  scrollWrapper();
  setHeight();
}

// COMPUTEDS
const selectedLanguage = computed(() => {
  return useNuxtApp().$i18n.locale;
});

const emit = defineEmits(["toggleVisible"]);
const count = ref(0);
const focusOut = (event: MouseEvent) => {
  if (count.value === 0) {
    count.value++;
    return;
  }
  const target = event.target as HTMLElement;
  //@ts-ignore
  if (!container.value.contains(target)) emit("toggleVisible");
};
onBeforeMount(() => {
  document.addEventListener("click", focusOut);
});
onUnmounted(() => {
  document.removeEventListener("click", focusOut);
});
</script>
<template>
  <div ref="container" class="ui-config">
    <div ref="wrapper" class="ui-config__content">
      <div class="ui-config__content__tab">
        <div class="ui-config__option">
          <div>
            <MGIcon icon="moon" />
            {{ $t("header.menus.dark_mode") }}
          </div>
          <label class="switch">
            <input v-model="theme" type="checkbox" :true-value="'dark'" :false-value="'light'" />
            <span class="slider"></span>
          </label>
        </div>
        <div class="ui-config__option" @click="changeTab(1)">
          <div>
            <MGIcon icon="region" />
            {{ $t("header.menus.country") }}
          </div>
          <div>
            <MGIcon icon="chevron-right" class="m-0" />
          </div>
        </div>
      </div>
      <div class="ui-config__content__tab">
        <div class="ui-config__option" @click="changeTab(0)">
          <div>
            <MGIcon icon="chevron-left" />
            {{ $t("community.buttons.back") }}
          </div>
        </div>
        <p>{{ $t("header.menus.select_country_message") }}</p>
        <NuxtLink v-for="(item, index) in tempLanguages" :key="'select_languages_' + index"
          :to="switchLocalePath(item.name_abrev as string)" :class="{ active: selectedLanguage === item.name_abrev }"
          class="ui-config__option">
          <div class="w-100">
            <img :src="item.flag_url" class="me-1" alt="" /> {{ $t(item.name) }}
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.logout-option {
  color: var(--sidebar-menus-fg);
  text-decoration: none;

  &:hover {
    background-color: var(--header-ui-config-hover);
    color: var(--sidebar-menus-fg);
  }
}

.ui-config {
  position: absolute;
  max-width: 240px;
  width: 240px;
  padding: 1rem 0;
  background-color: var(--header-ui-config-bg);
  border: 1px solid var(--header-ui-config-border);
  top: 45px;
  right: 0;
  z-index: 9999;
  overflow: hidden;
  transition: background ease 0.3s;
  --animate-duration: 0.5s;

  &__option {
    color: var(--sidebar-menus-fg);
    display: flex;
    justify-content: space-between;
    padding: 0 1rem;
    font-size: 0.875rem;
    min-height: 48px;
    align-items: center;
    cursor: pointer;

    img {
      max-width: 16px;
      display: inline-block;
    }

    &:hover {
      background-color: var(--header-ui-config-hover);
    }

    &>div {
      .icon {
        display: inline-block;
        vertical-align: middle;
        font-size: 1.5rem;
        margin-right: 0.5rem;
      }
    }

    a {
      .icon {
        font-size: 1rem !important;
      }
    }
  }

  &__content {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    /* will-change: transform; */
    transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);

    &__tab {
      flex: 1 0 100%;
      width: 100%;
      background-color: var(--header-ui-config-bg);
      transition: background-color ease 0.3s;

      p {
        color: var(--active);
        font-size: 12px;
        padding: 0.5rem 1rem;
        margin: 0;
      }
    }
  }
}
</style>
